import Api from '@/services/Api';

const DASHBOARD_LANGUAGE_MAP = {
  es: 'SPA',
  en: 'ENG',
  fr: 'FRA',
  pt: 'POR',
  it: 'ITA',
};

const COMPLAINTS_LANGUAGE_MAP = {
  es: 'SPA',
  en: 'ENG',
  fr: 'FRA',
  pt: 'POR',
  it: 'ITA',
};

function parseJsonResponse(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function normalizeLanguageCode(language) {
  const normalizedLanguage = String(language || 'es')
    .toLowerCase()
    .split('-')[0];

  return normalizedLanguage;
}

function mapDashboardLanguage(language) {
  const normalizedLanguage = normalizeLanguageCode(language);

  return DASHBOARD_LANGUAGE_MAP[normalizedLanguage] || 'SPA';
}

function mapComplaintsLanguage(language) {
  const normalizedLanguage = normalizeLanguageCode(language);

  return COMPLAINTS_LANGUAGE_MAP[normalizedLanguage] || 'ESP';
}

function getCurrentLanguage(rootState, rootGetters) {
  return (
    rootGetters['user/getLanguage'] ||
    rootState.user?.languageInterface ||
    rootState.user?.authenticatedUser?.language_code ||
    rootState.user?.authenticatedUser?.contacts?.[0]?.language ||
    'es'
  );
}

function normalizeCounter(value) {
  const counter = Number(value);

  return Number.isFinite(counter) ? counter : 0;
}

function normalizeDashboardResponse(response) {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const issuesData = response.issues_data;
  const issuesByState = issuesData?.issues_by_state;

  if (!issuesData || !issuesByState) {
    return null;
  }

  const rojos = normalizeCounter(issuesByState.rojos);
  const amarillos = normalizeCounter(issuesByState.amarillos);
  const verdes = normalizeCounter(issuesByState.verdes);
  const responseTotal = Number(issuesByState.total);
  const total = Number.isFinite(responseTotal)
    ? responseTotal
    : rojos + amarillos + verdes;

  return {
    total,
    issuesByCause: issuesData.issues_by_cause || {},
    issuesByState,
    titulo: response?.titulo,
    textoBotonAlta: response?.textoBotonAlta,
    textoBotonHistorial: response?.textoBotonHistorial,
    mensajeVacioTitulo: response?.mensajeVacioTitulo,
    mensajeVacioPrincipal: response?.mensajeVacioPrincipal,
    mensajeVacioSecundario: response?.mensajeVacioSecundario,
    mensajeVacioAyuda: response?.mensajeVacioAyuda,
    tarjetas: [
      {
        key: 'pendingAction',
        contador: rojos,
        estado: 'pending_action',
        variant: 'pending',
      },
      {
        key: 'inProgress',
        contador: amarillos,
        estado: 'in_progress',
        variant: 'progress',
      },
      {
        key: 'resolved',
        contador: verdes,
        estado: 'resolved',
        variant: 'resolved',
      },
    ],
  };
}

export default {
  namespaced: true,
  state: {
    complaints: [], // Almacenará las incidencias obtenidas del servicio
    complaintAgent: '', // Almacenará el comercial asignado al cliente actual
    selectedComplaint: '', // Almacenará el código de la incidencia actual
    complaintDetails: [], // Almacenará los detalles de la incidencia actual
    complaintFiles: [], // Almacenará el ZIP con los archivos adjuntos de la incidencia actual, si hubiera
    complaintReport: [], // Almacenará el ZIP con el informe de contestación de la incidencia actual, si hubiera
    origins: [], // Almacenará las posibles causas de las incidencias
    newComplaintCode: '', // Almacenará el código de la nueva incidencia registrada por si hubiera que adjuntar archivos
    complaintDashboard: null, // Almacenará los datos del dashboard de quejas
    complaintFilesLoading: false,
    complaintReportLoading: false,
    complaintDashboardLoading: false, // Indicador de carga para el dashboard de quejas
    complaintDashboardError: null, // Almacenará el error ocurrido al cargar el dashboard de quejas, si hubiera
  },
  actions: {
    async fetchComplaintDashboard({
      commit,
      rootState,
      rootGetters,
    }) {
      const companyCode = rootState.contact?.company;

      commit('setComplaintDashboardLoading', true);
      commit('setComplaintDashboardError', null);

      if (!companyCode) {
        commit('setComplaintDashboard', null);
        commit('setComplaintDashboardLoading', false);
        return null;
      }

      try {
        const codIdioma = mapDashboardLanguage(
          getCurrentLanguage(rootState, rootGetters)
        );
        const xhr = await Api.get_complaints_dashboard(
          companyCode,
          codIdioma
        );
        const response = parseJsonResponse(xhr?.response);

        const normalizedDashboard =
          normalizeDashboardResponse(response);

        if (!normalizedDashboard) {
          throw new Error('invalid_complaint_dashboard_response');
        }

        commit('setComplaintDashboard', normalizedDashboard);
        return normalizedDashboard;
      } catch (error) {
        commit('setComplaintDashboard', null);
        commit(
          'setComplaintDashboardError',
          error?.message || 'complaint_dashboard_error'
        );
        return Promise.reject(error);
      } finally {
        commit('setComplaintDashboardLoading', false);
      }
    },
    async fetchComplaintList({ commit, rootState, rootGetters }) {
      try {
        const codIdioma = mapComplaintsLanguage(
          getCurrentLanguage(rootState, rootGetters)
        );
        // Se obtiene la lista de incidencias del cliente actual por código de cliente
        const xhr = await Api.get_complaints_list(
          rootState.contact.company,
          codIdioma
        );
        const response = JSON.parse(xhr.response);
        commit('setComplaints', response);
        // Se obtiene el comercial asignado al cliente actual para el detalle de incidencias
        if (rootState.contact?.addresses?.[0]?.agent) {
          commit(
            'setComplaintAgent',
            rootState.contact.addresses[0].agent
          );
        }
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchComplaintDetails(
      { commit, rootState },
      { selectedComplaint }
    ) {
      try {
        // Reseteo de datos
        commit('setComplaintDetails', []);
        commit('setSelectedComplaint', '');
        const xhr = await Api.get_complaint_details(
          selectedComplaint.title,
          rootState.contact.company
        );
        const response = JSON.parse(xhr.response);
        commit('setComplaintDetails', response.mensaje[0]);
        commit('setSelectedComplaint', selectedComplaint);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchComplaintFilesZip({ commit }, { codigoIncidencia }) {
      commit('setComplaintFilesLoading', true);
      try {
        commit('setComplaintFiles', []);
        const result = await Api.get_complaint_files(
          codigoIncidencia
        );

        // Si no hay archivos, se muestra un mensaje
        // Si hay archivos, se muestra el zip
        if (result.type === 'json') {
          commit('setComplaintFiles', [
            {
              tieneArchivos: false,
              mensaje: result.data.mensaje,
            },
          ]);
        } else if (result.type === 'zip') {
          commit('setComplaintFiles', [
            {
              tieneArchivos: true,
              filename: `Archivos-${codigoIncidencia}.zip`,
              fileBase64: result.data.fileBase64,
            },
          ]);
        }

        return Promise.resolve(result.data);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setComplaintFilesLoading', false);
      }
    },
    async fetchComplaintReportZip({ commit }, { codigoIncidencia }) {
      commit('setComplaintReportLoading', true);
      try {
        commit('setComplaintReport', []);
        const result = await Api.get_complaint_report(
          codigoIncidencia
        );

        // Si no hay informe de contestación, se muestra un mensaje
        // Si hay informe, se muestra el zip
        if (result.type === 'json') {
          commit('setComplaintReport', [
            { tieneArchivos: false, mensaje: result.data.mensaje },
          ]);
        } else if (result.type === 'zip') {
          commit('setComplaintReport', [
            {
              tieneArchivos: true,
              filename: `Informe-${codigoIncidencia}.zip`,
              fileBase64: result.data.fileBase64,
            },
          ]);
        }

        return Promise.resolve(result.data);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setComplaintReportLoading', false);
      }
    },
    async fetchOrigins({ commit, rootState, rootGetters }) {
      try {
        const codIdioma = mapComplaintsLanguage(
          getCurrentLanguage(rootState, rootGetters)
        );
        const xhr = await Api.get_complaints_origins(codIdioma);
        const response = JSON.parse(xhr.response);

        // Mapeo manual de los títulos a claves de traducción
        const mapped = response.map((item) => {
          let titleKey = '';

          switch (item.title) {
            case 'Resultados de Ensayo':
              titleKey = 'TestResults';
              break;
            case 'Petición del Cliente':
              titleKey = 'CustomerRequest';
              break;
            case 'Tiempos de Respuesta':
              titleKey = 'ResponseTimes';
              break;
            case 'Oferta':
              titleKey = 'Quote';
              break;
            case 'Error Ingreso Muestra':
              titleKey = 'SampleEntryError';
              break;
            case 'Informe Incorrecto':
              titleKey = 'IncorrectReport';
              break;
            case 'Opinión sobre la Atención':
              titleKey = 'CustomerServiceFeedback';
              break;
            default:
              titleKey = null;
          }

          return {
            ...item,
            titleKey,
          };
        });

        commit('setOrigins', mapped);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async registerComplaint({ commit, rootState }, data) {
      try {
        commit('setNewComplaintCode', '');
        // Se añaden los datos del cliente y la fecha actual
        // Se envía el cod cliente y como país los 4 primeros caracteres
        data.Cliente = rootState.contact.company;
        data.CodCliente = rootState.contact.company;
        data.PaisCliente =
          rootState.contact.company?.substring(0, 4) || '';

        // Se añade el nombre del comercial asignado para adjuntar a la descripción en la API Quejas
        if (rootState.contact?.addresses?.[0]?.agent) {
          data.ComercialAsignado =
            rootState.contact.addresses[0]?.agent;
        }
        const xhr = await Api.post_new_complaint(data);
        const response = await xhr.json();
        // Si hay un error en la respuesta, se lanza para el catch
        if (response.error) {
          throw new Error(response.error);
        }
        // Si va bien, se almacena el código de la nueva incidencia para poder adjuntar archivos
        commit('setNewComplaintCode', response.text);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async attachComplaintFiles(context, data) {
      try {
        const xhr = await Api.post_new_complaint_files(data);
        const response = await xhr.json();
        // Si hay un error en la respuesta, se lanza para el catch
        if (response.error) {
          throw new Error(response.error);
        }
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setComplaints({ commit }, value) {
      commit('setComplaints', value);
    },
    setComplaintAgent({ commit }, value) {
      commit('setComplaintAgent', value);
    },
    setSelectedComplaint({ commit }, value) {
      commit('setSelectedComplaint', value);
    },
    setComplaintDetails({ commit }, value) {
      commit('setComplaintDetails', value);
    },
    setComplaintFiles({ commit }, value) {
      commit('setComplaintFiles', value);
    },
    setComplaintReport({ commit }, value) {
      commit('setComplaintReport', value);
    },
    setOrigins({ commit }, value) {
      commit('setOrigins', value);
    },
    setNewComplaintCode({ commit }, value) {
      commit('setNewComplaintCode', value);
    },
    setComplaintDashboard({ commit }, value) {
      commit('setComplaintDashboard', value);
    },
  },
  mutations: {
    setComplaints(state, value) {
      state.complaints = value;
    },
    setComplaintAgent(state, value) {
      state.complaintAgent = value;
    },
    setSelectedComplaint(state, value) {
      state.selectedComplaint = value;
    },
    setComplaintDetails(state, value) {
      state.complaintDetails = value;
    },
    setComplaintFiles(state, value) {
      state.complaintFiles = value;
    },
    setComplaintFilesLoading(state, value) {
      state.complaintFilesLoading = value;
    },
    setComplaintReport(state, value) {
      state.complaintReport = value;
    },
    setComplaintReportLoading(state, value) {
      state.complaintReportLoading = value;
    },
    setOrigins(state, value) {
      state.origins = value;
    },
    setNewComplaintCode(state, value) {
      state.newComplaintCode = value;
    },
    setComplaintDashboard(state, value) {
      state.complaintDashboard = value;
    },
    setComplaintDashboardLoading(state, value) {
      state.complaintDashboardLoading = value;
    },
    setComplaintDashboardError(state, value) {
      state.complaintDashboardError = value;
    },
    resetData(state) {
      state.complaints = [];
      state.complaintAgent = '';
      state.selectedComplaint = '';
      state.complaintDetails = [];
      state.complaintFiles = [];
      state.complaintFilesLoading = false;
      state.complaintReport = [];
      state.complaintReportLoading = false;
      state.origins = [];
      state.newComplaintCode = '';
      state.complaintDashboard = null;
      state.complaintDashboardLoading = false;
      state.complaintDashboardError = null;
    },
  },
};
